// App specific
import listApi from '../api/list-api';

// Cache configuration
const CACHE_DURATION_MINUTES = 10;
const CACHE_DURATION_MS = CACHE_DURATION_MINUTES * 60 * 1000;

// helpers
const setCachedListDocuments = (data) => {
  localStorage.setItem('cachedListDocuments', JSON.stringify({
    datetime: new Date(),
    data,
  }));
};

// initial state
const state = {
  listsActive: null,
  listsArchived: null,
  currentList: null,
  formVisible: false,
  currentListItems: [],
  userListDocuments: {},
};

// getters
const getters = {
  listsArchived: (state) => state.listsArchived,
  listsActive: (state) => state.listsActive,
  listCurrent: (state) => state.currentList,
  listCurrentItems: (state) => state.currentListItems,
  userListDocuments: (state) => state.userListDocuments,
};

// actions
const actions = {

  async setDefaultListState({ commit }) {
    commit('setDefaultListState');
  },

  async getList({ commit, dispatch }, id) {
    const list = await listApi.get(id);
    commit('setCurrentList', list.data);
    dispatch('getListItems', id);
    return list;
  },

  async getListExportHTMLPreviewToken({ getters }) {
    const { listCurrent: currentList } = getters;
    const result = await listApi.getListExportHTMLPreviewToken(currentList.id);
    return result.data;
  },

  async getListItems({ commit }, listId) {
    const listItems = await listApi.getListItems(listId);
    commit('setCurrentListItems', listItems.data);
    return listItems;
  },

  async removeCurrentList({ dispatch, getters }) {
    const { listCurrent: currentList } = getters;

    await listApi.delete(currentList);

    if (currentList.archived) {
      dispatch('getArchivedLists');
    } else {
      dispatch('getActiveLists');
    }
  },

  async getArchivedLists({ commit, dispatch }, selectFirst = true) {
    const lists = await listApi.getArchived();
    commit('setListsArchived', lists.data);
    if (selectFirst && lists.data.length > 0) {
      commit('setCurrentList', lists.data[0]);
      dispatch('getListItems', lists.data[0].id);
    }
    return lists;
  },

  async getActiveLists({ commit, dispatch }, selectFirst = true) {
    const lists = await listApi.getActive();
    commit('setListsActive', lists.data);
    if (selectFirst && lists.data.length > 0) {
      commit('setCurrentList', lists.data[0]);
      dispatch('getListItems', lists.data[0].id);
    }
    return lists;
  },

  async saveList({ commit, dispatch }, list) {
    let response;

    if (list.id) {
      response = await listApi.update(list);
      if (list.archived) {
        dispatch('getArchivedLists', false);
      } else {
        dispatch('getActiveLists', false);
      }
      dispatch('getList', list.id);
    } else {
      response = await listApi.save(list);
      commit('addNewList', response.data);
      dispatch('getList', response.data.id);
    }
    return response.data;
  },

  async toggleListArchive({ dispatch, getters, commit }) {
    const { listCurrent: currentList } = getters;

    const listCurrentCopy = { ...currentList };
    listCurrentCopy.archived = !listCurrentCopy.archived;

    const updatedList = await listApi.update(listCurrentCopy);
    dispatch('getActiveLists', false);
    dispatch('getArchivedLists', false);
    commit('setCurrentList', listCurrentCopy);
    return updatedList;
  },

  async verifyTransferEmail(_, email) {
    return listApi.verify(email);
  },

  async transferLists({ dispatch }, {
    email,
    listIds,
    makeCopy,
    successString,
  }) {
    const transferResult = await listApi.transfer(email, listIds, makeCopy);

    // When successful, show the results
    const { status, data: transferredLists } = transferResult;
    const { config: { data: requestData } } = transferResult;
    const { email: targetEmail } = JSON.parse(requestData);
    const amountTransferred = transferredLists ? transferredLists.length : null;

    if (status === 200 && amountTransferred && targetEmail) {
      dispatch('setMessage', {
        message: `${amountTransferred} ${successString} ${targetEmail}`,
        type: 'success',
      });
    }

    dispatch('getActiveLists');
    return transferResult;
  },

  async sortListOnDate({ dispatch }, { listId, desc }) {
    const response = await listApi.sort(listId, desc);
    dispatch('getListItems', listId);
    return response.data;
  },

  async duplicateCurrentList({ dispatch, getters }) {
    const { listCurrent: currentList } = getters;
    const duplicatedList = await listApi.duplicate(currentList.id);
    dispatch('getActiveLists', false);
    dispatch('getArchivedLists', false);
    return duplicatedList.data;
  },

  async clearCurrentList({ dispatch, getters }) {
    const { listCurrent: currentList } = getters;
    const clearedList = await listApi.clearList(currentList.id);
    dispatch('getListItems', currentList.id);
    return clearedList.data;
  },

  async getListDocuments({ commit }, { skipCache = false } = {}) {
    // First check cached documents:
    if (!skipCache) {
      const cachedData = localStorage.getItem('cachedListDocuments');
      if (cachedData) {
        const { datetime, data } = JSON.parse(cachedData);
        // Use cache if within time limit
        const cacheTime = (new Date(datetime)).getTime();
        const cacheTimeLimit = (new Date()).getTime() - CACHE_DURATION_MS;
        const isCacheValid = cacheTime > cacheTimeLimit;

        if (isCacheValid) {
          commit('updateUserListDocuments', data);
          return data;
        }
      }
    }

    const response = await listApi.getListDocuments();
    commit('updateUserListDocuments', response.data);
    setCachedListDocuments(response.data);

    return response.data;
  },
};

// mutations
const mutations = {
  setDefaultListState(state) {
    state.currentList = null;
    state.currentListItems = [];
  },
  setListsArchived(state, lists) {
    state.listsArchived = lists;
  },
  setListsActive(state, lists) {
    state.listsActive = lists;
  },
  setCurrentList(state, list) {
    state.currentList = list;
  },
  setCurrentListItems(state, listsItems) {
    state.currentListItems = listsItems;
  },
  addNewList(state, list) {
    state.listsActive = [list, ...state.listsActive];
  },
  updateUserListDocuments(state, documents) {
    state.userListDocuments = documents;
    // Update cache with new state
    setCachedListDocuments(documents);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
